import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

const CLIENT_LINKS = [
  {
    label: 'Dashboard', path: '/central',
    icon: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
  },
  {
    label: 'Activate Appliance', path: '/central/activate',
    icon: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/>,
  },
  {
    label: 'My Licenses', path: '/central/licenses',
    icon: <><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/></>,
  },
  {
    label: 'Extend Warranty', path: '/central/warranty',
    icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
  },
  {
    label: 'Support Tickets', path: '/central/support',
    icon: <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>,
  },
];

const CLIENT_ACCOUNT_LINKS = [
  {
    label: 'Profile', path: '/central/profile',
    icon: <><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></>,
  },
];

// Admin sidebar: grouped by section
const ADMIN_SECTIONS = [
  {
    label: 'Main',
    links: [
      {
        label: 'Admin Dashboard', path: '/admin',
        icon: <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>,
      },
    ],
  },
  {
    label: 'Products',
    links: [
      {
        label: 'Products', path: '/admin/products', badge: true,
        icon: <><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></>,
      },
      {
        label: 'Import Excel', path: '/admin/import',
        icon: <><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></>,
      },
      {
        label: 'Certificates', path: '/admin/certificates',
        icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></>,
      },
    ],
  },
  {
    label: 'Clients',
    links: [
      {
        label: 'Organizations', path: '/admin/organizations',
        icon: <><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>,
      },
      {
        label: 'Users', path: '/admin/users',
        icon: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>,
      },
    ],
  },
  {
    label: 'Support',
    links: [
      {
        label: 'Messages', path: '/admin/messages',
        icon: <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>,
      },
      {
        label: 'Warranty', path: '/admin/warranty',
        icon: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>,
      },
      {
        label: 'Audit Log', path: '/admin/audit',
        icon: <><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></>,
      },
      {
        label: 'Help', path: '/admin/help',
        icon: <><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></>,
      },
    ],
  },
];

function NavIcon({ children, active }) {
  return (
    <svg className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'opacity-100' : 'opacity-60'}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
      {children}
    </svg>
  );
}

function SidebarLink({ link, isActive, navigate, productCount }) {
  const handleClick = (e) => {
    e.preventDefault();
    navigate(link.path);
  };

  return (
    <a
      href={link.path}
      onClick={handleClick}
      className={`relative flex items-center gap-[10px] px-3 py-2 rounded-[7px] text-[13px] font-[450] transition-all mb-px ${
        isActive
          ? 'bg-[rgba(6,182,212,0.12)] text-[#06b6d4] font-semibold'
          : 'text-[#64748b] hover:bg-[rgba(148,163,184,0.04)] hover:text-[#94a3b8]'
      }`}
    >
      {isActive && (
        <span className="absolute left-0 top-[5px] bottom-[5px] w-[2.5px] rounded-r bg-[#06b6d4]" />
      )}
      <NavIcon active={isActive}>{link.icon}</NavIcon>
      {link.label}
      {link.badge && productCount > 0 && (
        <span className="ml-auto text-[10px] bg-[rgba(6,182,212,0.12)] text-[#06b6d4] px-[7px] py-[1px] rounded-[10px] font-semibold">
          {productCount}
        </span>
      )}
    </a>
  );
}

export default function Sidebar() {
  const { user, isAdmin, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [productCount, setProductCount] = useState(0);

  useEffect(() => {
    if (!isAdmin) return;
    api.get('/admin/messages/unread-count')
      .then(({ data }) => setUnreadMessages(data.count))
      .catch(() => {});
    api.get('/admin/product-lines')
      .then(({ data }) => setProductCount(data.productLines?.length || 0))
      .catch(() => {});
    const interval = setInterval(() => {
      api.get('/admin/messages/unread-count')
        .then(({ data }) => setUnreadMessages(data.count))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  const isLinkActive = (link) => {
    if (link.path === '/central' || link.path === '/admin') {
      return location.pathname === link.path;
    }
    if (link.path === '/admin/certificates') {
      return location.pathname === '/admin/certificates';
    }
    if (link.path === '/admin/products') {
      return location.pathname.startsWith('/admin/products');
    }
    return location.pathname.startsWith(link.path);
  };

  return (
    <aside className="fixed left-0 top-0 bottom-0 w-60 bg-[#0a0f1a] border-r border-[rgba(148,163,184,0.06)] flex flex-col z-40">
      {/* Brand */}
      <div className="px-5 pt-[22px] pb-[18px] border-b border-[rgba(148,163,184,0.06)]">
        <a href={isAdmin ? '/admin' : '/central'} onClick={(e) => { e.preventDefault(); navigate(isAdmin ? '/admin' : '/central'); }} className="block">
          <h1 className="text-[15px] font-bold tracking-[1.8px] text-[#e2e8f0]">
            <span className="text-[#06b6d4] font-bold">TRITECH</span> CENTRAL
          </h1>
          <p className="text-[10px] text-[#475569] mt-[3px] tracking-[0.3px]">Appliance Management</p>
        </a>
      </div>

      {/* User info */}
      <div className="px-4 py-4 border-b border-[rgba(148,163,184,0.06)] flex items-center gap-[10px]">
        <div className="w-[34px] h-[34px] rounded-lg bg-gradient-to-br from-[#06b6d4] to-[#818cf8] flex items-center justify-center text-white text-[13px] font-bold flex-shrink-0">
          {user?.name?.charAt(0) || 'U'}
        </div>
        <div className="min-w-0">
          <h3 className="text-[12.5px] font-semibold text-[#e2e8f0] truncate">{user?.name}</h3>
          <p className="text-[10px] text-[#475569] mt-[1px] truncate">{user?.organization?.shortName || user?.organization?.name || user?.email}</p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-3 overflow-y-auto">
        {isAdmin ? (
          ADMIN_SECTIONS.map((section, si) => (
            <div key={section.label}>
              <div className={`text-[9.5px] font-semibold tracking-[1.5px] text-[#475569] uppercase px-3 py-2 pb-1 ${si > 0 ? 'mt-3' : ''}`}>
                {section.label}
              </div>
              {section.links.map(link => (
                <SidebarLink key={link.path} link={link} isActive={isLinkActive(link)} navigate={navigate} productCount={productCount} />
              ))}
            </div>
          ))
        ) : (
          <>
            <div className="text-[9.5px] font-semibold tracking-[1.5px] text-[#475569] uppercase px-3 py-2 pb-1">Main</div>
            {CLIENT_LINKS.map(link => (
              <SidebarLink key={link.path} link={link} isActive={isLinkActive(link)} navigate={navigate} productCount={0} />
            ))}
            <div className="text-[9.5px] font-semibold tracking-[1.5px] text-[#475569] uppercase px-3 py-2 pb-1 mt-3">Account</div>
            {CLIENT_ACCOUNT_LINKS.map(link => (
              <SidebarLink key={link.path} link={link} isActive={isLinkActive(link)} navigate={navigate} productCount={0} />
            ))}
          </>
        )}
      </nav>

      {/* Footer */}
      <div className="px-2 py-2 border-t border-[rgba(148,163,184,0.06)]">
        <a
          href="/"
          onClick={(e) => { e.preventDefault(); navigate('/'); }}
          className="flex items-center gap-[10px] px-3 py-2 rounded-[7px] text-[12px] text-[#475569] hover:text-[#94a3b8] hover:bg-[rgba(148,163,184,0.04)] transition-all"
        >
          <NavIcon>
            <circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
          </NavIcon>
          Back to Website
        </a>
        <button
          onClick={logout}
          className="w-full flex items-center gap-[10px] px-3 py-2 rounded-[7px] text-[12px] text-[#475569] hover:text-red-400 hover:bg-[rgba(148,163,184,0.04)] transition-all mt-px"
        >
          <NavIcon>
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
          </NavIcon>
          Logout
        </button>
      </div>
    </aside>
  );
}
